/**
 * Persistance best-effort des « Récents » de la palette Ctrl+K.
 *
 * On mémorise en localStorage les 5 dernières fiches OUVERTES depuis la palette
 * pour les reproposer AVANT toute frappe (cf 04-ux § Surface 4). C'est du confort
 * pur : toute erreur (mode privé, quota, JSON corrompu) est avalée et retourne une
 * liste vide — jamais de crash de la palette pour un cache d'agrément.
 *
 * NB sécurité : on ne stocke que des métadonnées d'affichage déjà autorisées par
 * le serveur (RLS + permission) au moment de la recherche. Reproposer un item ne
 * ré-accorde AUCUN droit : le clic renavigue vers `url_cible`, elle-même
 * re-contrôlée par l'API à l'ouverture de la fiche.
 */

import { rechercheResultatSchema, type RechercheResultat } from "@champ-erp/contracts";
import { z } from "zod";

import { tenantStorageKey } from "../../lib/tenant-query-key.js";

const STORAGE_KEY = "champ-erp:recherche-recents";
const MAX_RECENTS = 5;

// On valide via le schéma de contrat : un localStorage corrompu ou d'une version
// antérieure est simplement ignoré (safeParse → liste vide).
const recentsSchema = z.array(rechercheResultatSchema);

/** Lit les récents du domaine (liste vide si le scope n'est pas résolu). */
export function lireRecents(domaineId: string | null | undefined): RechercheResultat[] {
  try {
    const storageKey = tenantStorageKey(STORAGE_KEY, domaineId);
    if (!storageKey) return [];
    // La clé historique n'était pas tenant-scopée : impossible de savoir
    // à quel domaine elle appartenait, donc on la supprime sans la migrer.
    localStorage.removeItem(STORAGE_KEY);
    const brut = localStorage.getItem(storageKey);
    if (!brut) return [];
    const parse = recentsSchema.safeParse(JSON.parse(brut));
    return parse.success ? parse.data.slice(0, MAX_RECENTS) : [];
  } catch {
    return [];
  }
}

/** Ajoute une fiche aux récents du domaine courant. */
export function ajouterRecent(
  domaineId: string | null | undefined,
  resultat: RechercheResultat,
): void {
  try {
    const storageKey = tenantStorageKey(STORAGE_KEY, domaineId);
    if (!storageKey) return;
    const actuels = lireRecents(domaineId);
    // Dédoublonnage par identité d'entité (type + id) : une fiche rouverte
    // remonte en tête sans créer de doublon.
    const filtres = actuels.filter((r) => !(r.type === resultat.type && r.id === resultat.id));
    const prochains = [resultat, ...filtres].slice(0, MAX_RECENTS);
    localStorage.setItem(storageKey, JSON.stringify(prochains));
  } catch {
    // Best-effort : un échec d'écriture (quota, mode privé) est sans conséquence.
  }
}
